import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useRef } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { themes, countries, citiesByCountry, type Country, type Photographer, type Theme } from "@/data/photographers";
import { PhotographerProfileView } from "@/components/PhotographerProfileView";
import { SETTING_PHOTOGRAPHER_MAX_GALLERY, getSettingInt } from "@/lib/app-settings";
import { SHOOTING_SETTINGS, LANGUAGES } from "@/lib/creator-filters";
import { toWebP, safeContentType, assertUploadable, UPLOAD_MAX_INPUT_BYTES, isStaticImage, STATIC_IMAGE_ACCEPT, rotateImage, urlToFile } from "@/lib/image-convert";

export const Route = createFileRoute("/account/profile")({
  head: () => ({
    meta: [
      { title: "Mein Profil, OM Selected" },
      { name: "robots", content: "noindex" },
    ],
  }),
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/login" });
    return { userId: data.user.id, email: data.user.email ?? "" };
  },
  component: AccountProfilePage,
});

type ProfileRow = {
  id: string;
  slug: string;
  name: string;
  email: string | null;
  bio: string | null;
  region: string | null;
  themes: string[] | null;
  portrait_url: string | null;
  gallery_urls: string[] | null;
  gallery_themes: string[] | null;
  gallery_titles: string[] | null;
  gallery_captions: string[] | null;
  country: string | null;
  city: string | null;
  price_from: number | null;
  tfp: boolean | null;
  price_on_request: boolean | null;
  website_url: string | null;
  instagram_handle: string | null;
  user_id: string | null;
  published: boolean;
  portrait_position: string | null;
  portrait_zoom: number | null;
  shooting_settings: string[] | null;
  languages: string[] | null;
  max_gallery_images: number | null;
};

const SELECT = "id, slug, name, email, bio, region, themes, portrait_url, gallery_urls, gallery_themes, gallery_titles, gallery_captions, country, city, price_from, tfp, price_on_request, website_url, instagram_handle, user_id, published, portrait_position, portrait_zoom, shooting_settings, languages, max_gallery_images";

function AccountProfilePage() {
  const { userId, email } = Route.useRouteContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const own = await supabase.from("photographers").select(SELECT).eq("user_id", userId).maybeSingle();
      if (cancelled) return;
      if (own.data) { setProfile(own.data as ProfileRow); setLoading(false); return; }
      if (email) {
        const byMail = await supabase.from("photographers").select(SELECT).ilike("email", email).is("user_id", null).maybeSingle();
        if (cancelled) return;
        if (byMail.data) {
          const claim = await supabase.from("photographers").update({ user_id: userId }).eq("id", byMail.data.id).select(SELECT).maybeSingle();
          if (!cancelled && claim.data) { setProfile(claim.data as ProfileRow); setLoading(false); return; }
        }
      }
      // No photographer profile — if this user is a visagist, send them to the visagist editor.
      const vis = await supabase.from("visagists").select("id").or(`user_id.eq.${userId}${email ? `,email.ilike.${email}` : ""}`).limit(1).maybeSingle();
      if (cancelled) return;
      if (vis.data) { navigate({ to: "/account/visagist" }); return; }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId, email, navigate]);

  if (loading) return <div className="mx-auto max-w-[800px] px-5 py-32 text-center text-muted-foreground italic">Lädt …</div>;

  if (!profile) {
    return (
      <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
        <h1 className="serif text-4xl">Noch kein freigeschaltetes Profil</h1>
        <p className="mt-6 text-muted-foreground">
          Wir konnten kein veröffentlichtes Profil finden, das mit deiner E-Mail ({email || ""}) verknüpft ist.
          Bewirb dich unten, sobald wir freischalten, kannst du dein Profil hier bearbeiten.
        </p>
        <div className="mt-10 flex justify-center gap-3 eyebrow">
          <Link to="/apply" className="border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors">Bewerben</Link>
          <Link to="/" className="border border-border px-5 py-3 hover:border-foreground transition-colors">Zur Startseite</Link>
        </div>
      </div>
    );
  }

  return <ProfileEditor profile={profile} onSaved={(p) => setProfile(p)} />;
}

const inp = "w-full bg-transparent border-b border-border focus:border-foreground py-2 outline-none";

function Field({ label, children, className = "" }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`block ${className}`}>
      <div className="eyebrow text-muted-foreground mb-2">{label}</div>
      {children}
    </div>
  );
}

function ProfileEditor({ profile, onSaved }: { profile: ProfileRow; onSaved: (p: ProfileRow) => void }) {
  const { userId } = Route.useRouteContext();
  const [name, setName] = useState(profile.name);
  const [contactEmail, setContactEmail] = useState(profile.email ?? "");
  const [bio, setBio] = useState(profile.bio ?? "");
  const [country, setCountry] = useState<Country | "">((profile.country as Country) ?? "");
  const [cityChoice, setCityChoice] = useState<string>(() => {
    if (!profile.city) return "";
    const list = profile.country ? citiesByCountry[profile.country as Country] ?? [] : [];
    return list.includes(profile.city) ? profile.city : "__other__";
  });
  const [cityOther, setCityOther] = useState<string>(() => {
    if (!profile.city) return "";
    const list = profile.country ? citiesByCountry[profile.country as Country] ?? [] : [];
    return list.includes(profile.city) ? "" : profile.city;
  });
  const [priceFrom, setPriceFrom] = useState<string>(profile.price_from?.toString() ?? "");
  const [tfp, setTfp] = useState<boolean>(profile.tfp ?? false);
  const [priceOnRequest, setPriceOnRequest] = useState<boolean>(profile.price_on_request ?? false);
  const [websiteUrl, setWebsiteUrl] = useState(profile.website_url ?? "");
  const [instagram, setInstagram] = useState(profile.instagram_handle ?? "");
  const [portraitUrl, setPortraitUrl] = useState(profile.portrait_url ?? "");
  const initialPos = (profile.portrait_position ?? "50% 50%").split(" ");
  const parsedX = parseFloat(initialPos[0] ?? "");
  const parsedY = parseFloat(initialPos[1] ?? "");
  const [portraitX, setPortraitX] = useState<number>(Number.isFinite(parsedX) ? parsedX : 50);
  const [portraitY, setPortraitY] = useState<number>(Number.isFinite(parsedY) ? parsedY : 50);
  const [portraitZoom, setPortraitZoom] = useState<number>(Number(profile.portrait_zoom ?? 1));
  const portraitFrameRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ active: boolean }>({ active: false });
  const [selectedThemes, setSelectedThemes] = useState<string[]>(profile.themes ?? []);
  const [shootingSettings, setShootingSettings] = useState<string[]>(profile.shooting_settings ?? []);
  const [languages, setLanguages] = useState<string[]>(profile.languages ?? []);
  const [galleryUrls, setGalleryUrls] = useState<string[]>(profile.gallery_urls ?? []);
  const [galleryThemes, setGalleryThemes] = useState<string[]>(() => {
    const urls = profile.gallery_urls ?? [];
    const tags = profile.gallery_themes ?? [];
    return urls.map((_, i) => tags[i] ?? "open");
  });
  const [galleryTitles, setGalleryTitles] = useState<string[]>(() => {
    const urls = profile.gallery_urls ?? [];
    const titles = profile.gallery_titles ?? [];
    return urls.map((_, i) => titles[i] ?? "");
  });
  const [galleryCaptions, setGalleryCaptions] = useState<string[]>(() => {
    const urls = profile.gallery_urls ?? [];
    const caps = profile.gallery_captions ?? [];
    return urls.map((_, i) => caps[i] ?? "");
  });
  const [newGalleryUrl, setNewGalleryUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [uploadingPortrait, setUploadingPortrait] = useState(false);
  const [maxGallery, setMaxGallery] = useState<number>(10);
  const [globalDefault, setGalleryDefault] = useState<number>(10);
  const [rotatingPortrait, setRotatingPortrait] = useState(false);
  const [rotatingIndex, setRotatingIndex] = useState<number | null>(null);

  useEffect(() => {
    getSettingInt(SETTING_PHOTOGRAPHER_MAX_GALLERY, 10).then((d) => {
      setGalleryDefault(d);
      if (profile.max_gallery_images == null) setMaxGallery(d);
    });
    if (profile.max_gallery_images != null) setMaxGallery(profile.max_gallery_images);
  }, [profile.max_gallery_images]);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  function clamp(n: number, min = 0, max = 100) { return Math.max(min, Math.min(max, n)); }

  function moveGalleryItem(from: number, to: number) {
    if (from === to || from < 0 || to < 0) return;
    const reorder = <T,>(arr: T[]): T[] => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    };
    setGalleryUrls((cur) => reorder(cur));
    setGalleryThemes((cur) => reorder(cur));
    setGalleryTitles((cur) => reorder(cur));
    setGalleryCaptions((cur) => reorder(cur));
  }

  function applyDragFromEvent(clientX: number, clientY: number) {
    const el = portraitFrameRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clamp(((clientX - rect.left) / rect.width) * 100);
    const y = clamp(((clientY - rect.top) / rect.height) * 100);
    setPortraitX(Math.round(x));
    setPortraitY(Math.round(y));
  }

  function onPortraitPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!portraitUrl) return;
    dragRef.current.active = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }
  function onPortraitPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragRef.current.active) return;
    applyDragFromEvent(e.clientX, e.clientY);
  }
  function onPortraitPointerUp() { dragRef.current.active = false; }

  async function uploadFile(file: File): Promise<string> {
    const optimized = await toWebP(file);
    assertUploadable(optimized);
    const ext = (optimized.name.split(".").pop() || "webp").toLowerCase().replace(/[^a-z0-9]/g, "") || "webp";
    const path = `${userId}/${profile.id}/${crypto.randomUUID()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("photographer-images")
      .upload(path, optimized, { cacheControl: "31536000", upsert: false, contentType: safeContentType(optimized) });
    if (upErr) throw upErr;
    const { data } = supabase.storage.from("photographer-images").getPublicUrl(path);
    return data.publicUrl;
  }

  async function onPortraitFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isStaticImage(file)) { setError("Nur statische Bilddateien erlaubt (keine GIFs)."); return; }
    if (file.size > UPLOAD_MAX_INPUT_BYTES) { setError("Bild ist zu groß (max. 25 MB)."); return; }
    setError(null);
    setUploadingPortrait(true);
    try {
      const url = await uploadFile(file);
      setPortraitUrl(url);
    } catch (err: any) {
      setError(err?.message ?? "Upload fehlgeschlagen.");
    } finally {
      setUploadingPortrait(false);
    }
  }

  async function rotatePortrait() {
    if (!portraitUrl) return;
    setRotatingPortrait(true);
    setError(null);
    try {
      const file = await urlToFile(portraitUrl);
      const rotated = await rotateImage(file, 90);
      const url = await uploadFile(rotated);
      setPortraitUrl(url);
    } catch (err: any) {
      setError(err?.message ?? "Drehen fehlgeschlagen.");
    } finally {
      setRotatingPortrait(false);
    }
  }

  async function rotateGalleryImage(i: number) {
    const u = galleryUrls[i];
    if (!u) return;
    setRotatingIndex(i);
    setError(null);
    try {
      const file = await urlToFile(u);
      const rotated = await rotateImage(file, 90);
      const url = await uploadFile(rotated);
      setGalleryUrls((cur) => cur.map((x, idx) => (idx === i ? url : x)));
    } catch (err: any) {
      setError(err?.message ?? "Drehen fehlgeschlagen.");
    } finally {
      setRotatingIndex(null);
    }
  }

  async function onGalleryFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;
    const free = maxGallery - galleryUrls.length;
    if (free <= 0) { setError(`Maximal ${maxGallery} Galerie-Bilder.`); return; }
    const take = files.slice(0, free);
    setError(null);
    setUploadingGallery(true);
    try {
      for (const f of take) {
        if (!isStaticImage(f)) { setError(`"${f.name}": nur statische Bilddateien erlaubt.`); continue; }
        if (f.size > UPLOAD_MAX_INPUT_BYTES) { setError(`"${f.name}" ist zu groß (max. 25 MB).`); continue; }
        const url = await uploadFile(f);
        setGalleryUrls((cur) => (cur.length >= maxGallery ? cur : [...cur, url]));
        setGalleryThemes((cur) => (cur.length >= maxGallery ? cur : [...cur, "open"]));
        setGalleryTitles((cur) => (cur.length >= maxGallery ? cur : [...cur, ""]));
        setGalleryCaptions((cur) => (cur.length >= maxGallery ? cur : [...cur, ""]));
      }
    } catch (err: any) {
      setError(err?.message ?? "Upload fehlgeschlagen.");
    } finally {
      setUploadingGallery(false);
    }
  }

  const cities = country ? citiesByCountry[country] : [];
  const cityResolved = cityChoice === "__other__" ? cityOther.trim() : cityChoice;

  function toggleTheme(id: string) {
    setSelectedThemes((cur) => cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id]);
  }

  function toggleInList(setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) {
    setter((cur) => (cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]));
  }

  function addGalleryUrl() {
    const u = newGalleryUrl.trim();
    if (!u) return;
    if (galleryUrls.length >= maxGallery) { setError(`Maximal ${maxGallery} Galerie-Bilder.`); return; }
    setGalleryUrls((cur) => [...cur, u]);
    setGalleryThemes((cur) => [...cur, "open"]);
    setGalleryTitles((cur) => [...cur, ""]);
    setGalleryCaptions((cur) => [...cur, ""]);
    setNewGalleryUrl("");
  }

  const previewPhotographer = useMemo<Photographer>(() => ({
    id: profile.slug,
    name: name || "Dein Name",
    region: cityResolved || profile.region || "",
    themes: selectedThemes as Theme[],
    priceFrom: Number(priceFrom) || 0,
    priceTo: 0,
    tfp,
    priceOnRequest,
    equipment: [],
    bio,
    shoots: cityResolved || "",
    email: profile.email ?? "",
    portrait: portraitUrl || "",
    gallery: galleryUrls,
    galleryThemes: galleryThemes as Theme[],
    galleryTitles,
    galleryCaptions,
    country: country || null,
    city: cityResolved || null,
    websiteUrl: websiteUrl || null,
    instagramHandle: instagram || null,
    portraitPosition: `${portraitX}% ${portraitY}%`,
    portraitZoom: portraitZoom,
  }), [profile.slug, profile.region, profile.email, name, cityResolved, selectedThemes, priceFrom, tfp, priceOnRequest, bio, portraitUrl, galleryUrls, galleryThemes, galleryTitles, galleryCaptions, country, websiteUrl, instagram, portraitX, portraitY, portraitZoom]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const igRaw = instagram.trim().replace(/^@/, "");
    const schema = z.object({
      name: z.string().trim().min(2).max(120),
      email: z.string().trim().email().max(255).optional().or(z.literal("")),
      country: z.enum(["Deutschland", "Österreich", "Schweiz"]),
      city: z.string().trim().min(2).max(80),
      bio: z.string().trim().max(2000).optional().or(z.literal("")),
      website_url: z.string().trim().url().max(500).optional().or(z.literal("")),
      instagram_handle: z.string().trim().regex(/^[A-Za-z0-9_.]{1,30}$/).optional().or(z.literal("")),
      price_from: z.coerce.number().int().min(0).max(100000).optional(),
      portrait_url: z.string().trim().url().max(500).optional().or(z.literal("")),
      themes: z.array(z.string().max(40)).min(1).max(10),
    });
    const parsed = schema.safeParse({
      name, email: contactEmail.trim(), country, city: cityResolved,
      bio: bio || "",
      website_url: websiteUrl || "",
      instagram_handle: igRaw || "",
      price_from: priceFrom || undefined,
      portrait_url: portraitUrl || "",
      themes: selectedThemes,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Bitte alle Felder prüfen.");
      return;
    }
    const v = parsed.data;
    setSaving(true);
    const update = await supabase.from("photographers").update({
      name: v.name,
      email: v.email || null,
      country: v.country,
      city: v.city,
      region: v.city,
      bio: v.bio || null,
      website_url: v.website_url || null,
      instagram_handle: v.instagram_handle || null,
      price_from: v.price_from ?? null,
      tfp,
      price_on_request: priceOnRequest,
      portrait_url: v.portrait_url || null,
      themes: selectedThemes,
      gallery_urls: galleryUrls,
      gallery_themes: galleryThemes.slice(0, galleryUrls.length),
      gallery_titles: galleryTitles.slice(0, galleryUrls.length).map((t) => t.trim()),
      gallery_captions: galleryCaptions.slice(0, galleryUrls.length).map((t) => t.trim()),
      portrait_position: `${portraitX}% ${portraitY}%`,
      portrait_zoom: Number(portraitZoom.toFixed(2)),
      shooting_settings: shootingSettings,
      languages: languages,
    }).eq("id", profile.id).select(SELECT).maybeSingle();
    setSaving(false);
    if (update.error || !update.data) { setError(update.error?.message ?? "Speichern fehlgeschlagen."); return; }
    onSaved(update.data as ProfileRow);
    setSavedAt(new Date().toLocaleTimeString("de-DE"));
  }

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 pt-10 pb-6 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="eyebrow text-muted-foreground">Mein Profil</div>
            <h1 className="serif text-3xl md:text-5xl mt-3 leading-[0.95]">{profile.name}</h1>
            <div className="eyebrow text-muted-foreground mt-3 uppercase">
              {profile.published ? "Veröffentlicht" : "Nicht veröffentlicht"} · /{profile.slug}
            </div>
          </div>
          <Link
            to="/photographers/$id"
            params={{ id: profile.slug }}
            className="eyebrow border border-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors"
          >
            Öffentliches Profil ansehen
          </Link>
        </div>
      </section>

      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-10 grid lg:grid-cols-2 gap-10">
        <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6 content-start">
          <div className="sm:col-span-2 eyebrow text-muted-foreground">Bearbeiten</div>

          <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} required className={inp} /></Field>
          <Field label="Öffentliche Kontakt-E-Mail">
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              maxLength={255}
              placeholder="kontakt@example.com"
              className={inp}
            />
            <div className="text-xs text-muted-foreground mt-1">
              Wird im Profil angezeigt. Kann von deiner Login-E-Mail abweichen.
              {Route.useRouteContext().email && (
                <> Login: <span className="opacity-80">{Route.useRouteContext().email}</span></>
              )}
            </div>
          </Field>

          <Field label="Land">
            <select value={country} onChange={(e) => { setCountry(e.target.value as Country); setCityChoice(""); setCityOther(""); }} required className={inp}>
              <option value="">Bitte wählen</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="Stadt">
            <select value={cityChoice} onChange={(e) => setCityChoice(e.target.value)} disabled={!country} required className={inp}>
              <option value="">Bitte wählen</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="__other__">Andere …</option>
            </select>
            {cityChoice === "__other__" && (
              <input value={cityOther} onChange={(e) => setCityOther(e.target.value)} required maxLength={80} placeholder="Stadt eingeben" className={`${inp} mt-2`} />
            )}
          </Field>

          <Field label="Preis ab (€)"><input type="number" min={0} max={100000} value={priceFrom} onChange={(e) => setPriceFrom(e.target.value)} className={inp} /></Field>
          <Field label="Weitere Preisoptionen" className="sm:col-span-2">
            <div className="flex flex-wrap gap-6 mt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={tfp} onChange={(e) => setTfp(e.target.checked)} /> TFP
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={priceOnRequest} onChange={(e) => setPriceOnRequest(e.target.checked)} /> Auf Anfrage
              </label>
            </div>
          </Field>
          <Field label="Website"><input type="url" placeholder="https://…" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} className={inp} /></Field>
          <Field label="Instagram (ohne @)"><input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="dein.handle" className={inp} /></Field>
          <Field label="Wo fotografierst du?" className="sm:col-span-2">
            <div className="flex flex-wrap gap-2 mt-1">
              {SHOOTING_SETTINGS.map((s) => {
                const on = shootingSettings.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleInList(setShootingSettings, s)}
                    className={`eyebrow border px-3 py-1 transition-colors ${on ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Sprachen" className="sm:col-span-2">
            <div className="flex flex-wrap gap-2 mt-1">
              {LANGUAGES.map((l) => {
                const on = languages.includes(l);
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => toggleInList(setLanguages, l)}
                    className={`eyebrow border px-3 py-1 transition-colors ${on ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Portrait" className="sm:col-span-2">
            <div className="flex flex-wrap items-center gap-4">
              <div
                ref={portraitFrameRef}
                onPointerDown={onPortraitPointerDown}
                onPointerMove={onPortraitPointerMove}
                onPointerUp={onPortraitPointerUp}
                onPointerCancel={onPortraitPointerUp}
                className={`relative w-32 aspect-[4/5] border border-border bg-muted overflow-hidden ${portraitUrl ? "cursor-move touch-none select-none" : ""} flex items-center justify-center`}
                title={portraitUrl ? "Ziehen zum Verschieben" : undefined}
              >
                {portraitUrl ? (
                  <>
                    <img
                      src={portraitUrl}
                      alt="Portrait"
                      draggable={false}
                      className="w-full h-full object-cover pointer-events-none"
                      style={{
                        objectPosition: `${portraitX}% ${portraitY}%`,
                        transform: `scale(${portraitZoom})`,
                        transformOrigin: "center",
                      }}
                    />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-background bg-foreground/70 shadow"
                      style={{ left: `${portraitX}%`, top: `${portraitY}%` }}
                    />
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">Kein Bild</span>
                )}
              </div>
              <label className={`eyebrow border border-foreground px-4 py-2 cursor-pointer hover:bg-foreground hover:text-background transition-colors ${uploadingPortrait ? "opacity-50 pointer-events-none" : ""}`}>
                {uploadingPortrait ? "Lädt …" : (portraitUrl ? "Ersetzen" : "Bild hochladen")}
                <input type="file" accept={STATIC_IMAGE_ACCEPT} onChange={onPortraitFile} className="hidden" />
              </label>
              {portraitUrl && (
                <button
                  type="button"
                  onClick={rotatePortrait}
                  disabled={rotatingPortrait}
                  className="eyebrow border border-border px-4 py-2 hover:border-foreground transition-colors disabled:opacity-50"
                >
                  {rotatingPortrait ? "Dreht …" : "↻ Drehen"}
                </button>
              )}
              {portraitUrl && (
                <button type="button" onClick={() => setPortraitUrl("")} className="eyebrow text-muted-foreground hover:text-foreground">Entfernen</button>
              )}
            </div>
            {portraitUrl && (
              <div className="mt-4 grid sm:grid-cols-[auto_1fr_auto] items-center gap-3">
                <div className="eyebrow text-muted-foreground">Zoom {portraitZoom.toFixed(2)}×</div>
                <input
                  type="range" min={1} max={3} step={0.05}
                  value={portraitZoom}
                  onChange={(e) => setPortraitZoom(Number(e.target.value))}
                  className="w-full"
                />
                <button
                  type="button"
                  onClick={() => { setPortraitX(50); setPortraitY(50); setPortraitZoom(1); }}
                  className="eyebrow text-muted-foreground hover:text-foreground"
                >Zurücksetzen</button>
              </div>
            )}
            {portraitUrl && (
              <div className="mt-1 text-xs text-muted-foreground italic">Tipp: Im Vorschaubild ziehen, um den Bildausschnitt zu verschieben.</div>
            )}
            <input type="url" placeholder="…oder Bild-URL einfügen" value={portraitUrl} onChange={(e) => setPortraitUrl(e.target.value)} className={`${inp} mt-3`} />
          </Field>

          <Field label="Bio" className="sm:col-span-2">
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={5} maxLength={2000} className={`${inp} resize-y`} />
          </Field>

          <Field label="Kategorien" className="sm:col-span-2">
            <div className="flex flex-wrap gap-2">
              {themes.map((t) => {
                const active = selectedThemes.includes(t.id);
                return (
                  <button type="button" key={t.id} onClick={() => toggleTheme(t.id)}
                    className={`eyebrow border px-4 py-2 transition-colors ${active ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>
                    {t.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={`Galerie (${galleryUrls.length}/${maxGallery})`} className="sm:col-span-2">
            <div className="text-xs text-muted-foreground mb-3 border-l-2 border-border pl-3">
              {profile.max_gallery_images != null ? (
                <span>Individuelles Limit: <strong>{maxGallery}</strong> Bilder (vom Team für dich festgelegt). Standard wäre {globalDefault}.</span>
              ) : (
                <span>Limit: <strong>{maxGallery}</strong> Bilder (globaler Standard).</span>
              )}
              {" "}
              {galleryUrls.length >= maxGallery
                ? <strong>Limit erreicht.</strong>
                : <span>Noch <strong>{maxGallery - galleryUrls.length}</strong> Bild(er) frei.</span>}
            </div>
            {galleryUrls.length > 1 && (
              <div className="text-xs text-muted-foreground italic mb-3">
                Tipp: Ziehe ein Bild am „Verschieben"-Griff, um die Reihenfolge zu ändern. Nicht vergessen zu speichern.
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              {galleryUrls.map((u, i) => {
                const validIds = new Set(themes.map((t) => t.id));
                const slotThemes = Array.from(
                  new Set(
                    (galleryThemes[i] ?? "open")
                      .split("|")
                      .map((s) => s.trim().toLowerCase())
                      .filter((s) => s && validIds.has(s as any)),
                  ),
                );
                const toggleSlotTheme = (themeId: string) => {
                  setGalleryThemes((cur) => cur.map((t, idx) => {
                    if (idx !== i) return t;
                    const current = Array.from(
                      new Set(
                        (t ?? "open")
                          .split("|")
                          .map((s) => s.trim().toLowerCase())
                          .filter((s) => s && validIds.has(s as any)),
                      ),
                    );
                    let next: string[];
                    if (current.includes(themeId)) {
                      next = current.filter((x) => x !== themeId);
                    } else {
                      if (current.length >= 2) return t;
                      next = [...current, themeId];
                    }
                    return next.length ? next.join("|") : "open";
                  }));
                };
                return (
                  <div
                    key={`${u}-${i}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOverIndex(i); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragIndex !== null) moveGalleryItem(dragIndex, i);
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                    className={`flex flex-col gap-3 border p-3 transition-colors ${
                      dragOverIndex === i && dragIndex !== null && dragIndex !== i
                        ? "border-foreground bg-muted/40"
                        : "border-border"
                    } ${dragIndex === i ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        draggable
                        onDragStart={() => setDragIndex(i)}
                        onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                        className="eyebrow text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing select-none flex items-center gap-1 text-[10px]"
                        title="Ziehen, um die Reihenfolge zu ändern"
                      >
                        <span aria-hidden className="text-base leading-none">⠿</span> Verschieben
                      </div>
                      <span className="eyebrow text-muted-foreground text-[10px]">#{i + 1}</span>
                    </div>
                    <img
                      src={u}
                      alt=""
                      draggable={false}
                      className="w-full max-h-64 object-contain bg-muted"
                      onError={(e) => ((e.currentTarget as HTMLImageElement).style.opacity = "0.2")}
                    />
                    <button
                      type="button"
                      onClick={() => rotateGalleryImage(i)}
                      disabled={rotatingIndex === i}
                      className="eyebrow border border-border px-3 py-1 text-[10px] self-start hover:border-foreground transition-colors disabled:opacity-50"
                    >
                      {rotatingIndex === i ? "Dreht …" : "↻ Drehen"}
                    </button>
                    <input
                      type="text"
                      maxLength={120}
                      placeholder="Titel (optional)"
                      value={galleryTitles[i] ?? ""}
                      onChange={(e) => setGalleryTitles((cur) => cur.map((t, idx) => idx === i ? e.target.value : t))}
                      className="bg-transparent border-b border-border focus:border-foreground text-sm py-1 outline-none"
                    />
                    <textarea
                      rows={2}
                      maxLength={400}
                      placeholder="Kurztext (optional)"
                      value={galleryCaptions[i] ?? ""}
                      onChange={(e) => setGalleryCaptions((cur) => cur.map((t, idx) => idx === i ? e.target.value : t))}
                      className="bg-transparent border-b border-border focus:border-foreground text-xs py-1 outline-none resize-y"
                    />
                    <div>
                      <div className="eyebrow text-muted-foreground text-[10px] mb-2">
                        Kategorien · max. 2 ({slotThemes.length}/2)
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {themes.map((t) => {
                          const active = slotThemes.includes(t.id);
                          const disabled = !active && slotThemes.length >= 2;
                          return (
                            <button
                              type="button"
                              key={t.id}
                              onClick={() => toggleSlotTheme(t.id)}
                              disabled={disabled}
                              className={`eyebrow border px-2.5 py-1 text-[10px] transition-colors ${
                                active
                                  ? "bg-foreground text-background border-foreground"
                                  : disabled
                                    ? "border-border text-muted-foreground opacity-40 cursor-not-allowed"
                                    : "border-border hover:border-foreground"
                              }`}
                            >
                              {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setGalleryUrls((cur) => cur.filter((_, idx) => idx !== i));
                        setGalleryThemes((cur) => cur.filter((_, idx) => idx !== i));
                        setGalleryTitles((cur) => cur.filter((_, idx) => idx !== i));
                        setGalleryCaptions((cur) => cur.filter((_, idx) => idx !== i));
                      }}
                      className="eyebrow text-muted-foreground hover:text-foreground text-xs self-start"
                    >
                      Bild entfernen
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <label className={`eyebrow border border-foreground px-4 py-2 cursor-pointer hover:bg-foreground hover:text-background transition-colors ${uploadingGallery || galleryUrls.length >= maxGallery ? "opacity-50 pointer-events-none" : ""}`}>
                {uploadingGallery ? "Lädt …" : "Bilder hochladen"}
                <input type="file" accept={STATIC_IMAGE_ACCEPT} multiple onChange={onGalleryFiles} className="hidden" />
              </label>
              <span className="text-xs text-muted-foreground">JPG/PNG/WebP, max. 25 MB pro Bild</span>
            </div>
            <div className="flex gap-2 mt-3">
              <input type="url" placeholder="…oder URL einfügen" value={newGalleryUrl} onChange={(e) => setNewGalleryUrl(e.target.value)} className={inp} />
              <button type="button" onClick={addGalleryUrl} className="eyebrow border border-border px-4 py-2 hover:border-foreground transition-colors">Hinzufügen</button>
            </div>
          </Field>

          <div className="sm:col-span-2 sticky bottom-0 z-10 -mx-5 md:-mx-10 px-5 md:px-10 py-4 bg-background/95 backdrop-blur border-t border-border flex flex-wrap items-center gap-4">
            <button type="submit" disabled={saving} className="eyebrow bg-foreground text-background px-6 py-3 hover:opacity-90 transition-opacity disabled:opacity-50">
              {saving ? "Speichert …" : "Änderungen speichern"}
            </button>
            {error && <div className="text-sm text-destructive">{error}</div>}
            {!error && savedAt && <div className="text-sm text-muted-foreground italic">Gespeichert um {savedAt}</div>}
          </div>
        </form>

        <aside className="lg:sticky lg:top-24 self-start">
          <div className="eyebrow text-muted-foreground mb-3 flex items-center justify-between">
            <span>Live-Vorschau</span>
            <span className="italic normal-case tracking-normal">noch nicht gespeichert</span>
          </div>
          <div className="border border-border bg-background overflow-hidden">
            <div className="max-h-[80vh] overflow-y-auto">
              <PhotographerProfileView p={previewPhotographer} compact />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
